import { storage } from './localStorage';

/**
 * File object structure for backend API
 * Supports two formats:
 * 1. Upload format (with base64 data)
 * 2. Server format (with CDN URL)
 */
export interface FileData {
  // Upload format fields
  filename?: string;
  size?: number;
  data?: string; // base64 encoded

  // Server format fields
  id?: number;
  url?: string;

  // Common field
  mimeType: string;
}

/**
 * Reads a file and converts it to base64 encoded FileData object
 * @param file - File object from input
 * @returns Promise with FileData object or null if error
 */
export const fileToBase64 = (file: File): Promise<FileData | null> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const base64String = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = base64String.split(',')[1];

        const fileData: FileData = {
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
          data: base64Data
        };

        resolve(fileData);
      } catch (error) {
        resolve(null);
      }
    };

    reader.onerror = () => {
      resolve(null);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Downloads a file from FileData object (supports both formats)
 * @param fileData - FileData object with base64 data or URL
 */
export const downloadFile = async (fileData: FileData): Promise<void> => {
  try {
    // Server format with URL
    if (fileData.url) {
      await downloadFileFromUrl(fileData.url, getFileName(fileData));
      return;
    }

    // Upload format with base64 data
    if (fileData.data && fileData.filename) {
      // Convert base64 to blob
      const byteCharacters = atob(fileData.data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: fileData.mimeType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileData.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

/**
 * Downloads a file from URL with optional Bearer token authentication
 * @param url - File URL
 * @param filename - Filename for download
 */
export const downloadFileFromUrl = async (url: string, filename: string): Promise<void> => {
  try {
    const token = storage.getItem('token');

    const fetchOptions: RequestInit = {};
    if (token) {
      fetchOptions.headers = {
        'Authorization': `Bearer ${token}`
      };
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error('Failed to download file');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file from URL:', error);
  }
};

/**
 * Formats file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Checks if the mimeType is an image that can be displayed
 * @param mimeType - MIME type string
 * @returns true if it's a displayable image
 */
export const isDisplayableImage = (mimeType: string): boolean => {
  const displayableTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/svg+xml'
  ];
  return displayableTypes.includes(mimeType.toLowerCase());
};

/**
 * Gets the filename from FileData (works with both formats)
 * @param fileData - FileData object
 * @returns filename string
 */
export const getFileName = (fileData: FileData): string => {
  if (fileData.filename) {
    return fileData.filename;
  }
  if (fileData.id) {
    const extension = fileData.mimeType.split('/')[1] || '';
    return `file_${fileData.id}.${extension}`;
  }
  return 'file';
};

/** Matches IMAGE validation in flowy-core; generic FILE fields remain unrestricted. */
export const IMAGE_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif'];
export const isSupportedImageUpload = (file: File): boolean => IMAGE_UPLOAD_TYPES.includes(file.type.toLowerCase());
